import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE } from "../../config/api";
import { formatCurrency } from "../../config/currency";
import { parseApiError } from "../../utils/parseApiError";
import useSnackbar from "../../context/Snackbar/useSnackbar";

interface Store {
  store_id: number;
  store_name: string;
}

interface TodayFigures {
  date: string;
  store_id: number;
  cash_sales_pos: number;
  cash_sales_app_pickup: number;
  cash_sales_app_delivery: number;
  cash_in_total: number;
  cash_out_total: number;
  balance: number;
  order_count: number;
  total_sales_amount: number;
  payment_method_breakdown: Record<string, number>;
}

interface CashMovement {
  cash_movement_id: number;
  movement_type: "in" | "out";
  amount: number;
  reason: string;
  created_at: string;
}

interface DailyClosure {
  closure_id: number;
  closure_date: string;
  cash_sales_pos: number;
  cash_sales_app_pickup: number;
  cash_sales_app_delivery: number;
  cash_in_total: number;
  cash_out_total: number;
  closing_balance: number;
  order_count: number;
  total_sales_amount: number;
  closed_at: string;
}

const CashDrawerPage = () => {
  const [cookies] = useCookies();
  const snack = useSnackbar();
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [figures, setFigures] = useState<TodayFigures | null>(null);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [movementType, setMovementType] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editReason, setEditReason] = useState("");

  const [showCloseDayModal, setShowCloseDayModal] = useState(false);
  const [isClosingDay, setIsClosingDay] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState<DailyClosure[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Fetch the stores this user can act on — auto-selects when there's only
  // one (the common single-store Admin case), otherwise a dropdown lets an
  // Owner (or an Admin owning several stores) pick which drawer to view.
  useEffect(() => {
    authenticatedFetch(`${API_BASE}/store`, { method: "GET" }, cookies.auth)
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((data) => {
        const list: Store[] = data.data || data || [];
        setStores(list);
        if (list.length === 1) setStoreId(list[0].store_id);
      })
      .catch(() => setStores([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchToday = useCallback(async () => {
    if (!storeId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [todayRes, movementsRes] = await Promise.all([
        authenticatedFetch(`${API_BASE}/cash/today?store_id=${storeId}`, { method: "GET" }, cookies.auth),
        authenticatedFetch(`${API_BASE}/cash/movement?store_id=${storeId}`, { method: "GET" }, cookies.auth),
      ]);
      if (todayRes.ok) setFigures(await todayRes.json());
      else setError(await parseApiError(todayRes, "Failed to load today's figures"));
      if (movementsRes.ok) {
        const data = await movementsRes.json();
        setMovements(data.data || []);
      }
    } catch (err) {
      setError("Failed to load cash drawer data");
    } finally {
      setIsLoading(false);
    }
  }, [storeId, cookies.auth]);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  const submitMovement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeId) return;
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Amount must be a positive number");
      return;
    }
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/cash/movement`,
        {
          method: "POST",
          body: JSON.stringify({ store_id: storeId, movement_type: movementType, amount: parsedAmount, reason: reason.trim() }),
        },
        cookies.auth
      );
      if (!res.ok) {
        setError(await parseApiError(res, "Failed to log cash movement"));
        return;
      }
      setAmount("");
      setReason("");
      await fetchToday();
      snack.onResponse({ message: "Cash movement logged", status: 200 });
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to log cash movement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (m: CashMovement) => {
    setEditingId(m.cash_movement_id);
    setEditAmount(String(m.amount));
    setEditReason(m.reason);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: number) => {
    const parsedAmount = parseFloat(editAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Amount must be a positive number");
      return;
    }
    if (!editReason.trim()) {
      setError("Reason is required");
      return;
    }
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/cash/movement/${id}`,
        { method: "PATCH", body: JSON.stringify({ amount: parsedAmount, reason: editReason.trim() }) },
        cookies.auth
      );
      if (!res.ok) {
        setError(await parseApiError(res, "Failed to update cash movement"));
        return;
      }
      setEditingId(null);
      await fetchToday();
      snack.onResponse({ message: "Cash movement updated", status: 200 });
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to update cash movement");
    }
  };

  const closeDay = async () => {
    if (!storeId) return;
    setIsClosingDay(true);
    setError(null);
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/cash/close-day`,
        { method: "POST", body: JSON.stringify({ store_id: storeId }) },
        cookies.auth
      );
      if (!res.ok) {
        setError(await parseApiError(res, "Failed to close the day"));
        return;
      }
      setShowCloseDayModal(false);
      await fetchToday();
      snack.onResponse({ message: "Day closed successfully", status: 200 });
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to close the day");
    } finally {
      setIsClosingDay(false);
    }
  };

  const fetchHistory = useCallback(async () => {
    if (!storeId) return;
    setIsLoadingHistory(true);
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/cash/history?store_id=${storeId}`,
        { method: "GET" },
        cookies.auth
      );
      if (res.ok) {
        const data = await res.json();
        setHistory(data.data || []);
      }
    } catch (e) {
      // Best-effort — the history panel just shows nothing if this fails.
    } finally {
      setIsLoadingHistory(false);
    }
  }, [storeId, cookies.auth]);

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Cash Drawer</h1>
        {stores.length > 1 && (
          <select
            className={style.storeSelect}
            value={storeId ?? ""}
            onChange={(e) => setStoreId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a store…</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
            ))}
          </select>
        )}
        <button
          className={style.historyBtn}
          onClick={() => { setShowHistoryModal(true); fetchHistory(); }}
          disabled={!storeId}
        >
          History
        </button>
        <button
          className={style.closeDayBtn}
          onClick={() => setShowCloseDayModal(true)}
          disabled={!storeId}
        >
          Close Day
        </button>
        <button className={style.refreshBtn} onClick={fetchToday} disabled={isLoading || !storeId}>
          {isLoading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && <div className={style.errorBanner}>{error}</div>}

      {!storeId ? (
        <div className={style.loadingState}>Select a store to view its cash drawer.</div>
      ) : !figures ? (
        <div className={style.loadingState}>Loading…</div>
      ) : (
        <>
          <div className={style.balanceCard}>
            <div className={style.balanceLabel}>Today's Drawer Balance</div>
            <div className={style.balanceValue}>{formatCurrency(figures.balance)}</div>
            <div className={style.balanceSub}>
              {figures.order_count} order{figures.order_count === 1 ? "" : "s"} · {formatCurrency(figures.total_sales_amount)} total sales today
            </div>
          </div>

          <div className={style.summaryCards}>
            <div className={style.card}>
              <div className={style.cardLabel}>POS Walk-in (Cash)</div>
              <div className={style.cardValue}>{formatCurrency(figures.cash_sales_pos)}</div>
            </div>
            <div className={style.card}>
              <div className={style.cardLabel}>App Pickup (Cash)</div>
              <div className={style.cardValue}>{formatCurrency(figures.cash_sales_app_pickup)}</div>
            </div>
            <div className={style.card}>
              <div className={style.cardLabel}>App Delivery (Cash)</div>
              <div className={style.cardValue}>{formatCurrency(figures.cash_sales_app_delivery)}</div>
            </div>
            <div className={style.card}>
              <div className={style.cardLabel}>Cash In / Out</div>
              <div className={style.cardValue}>
                <span className={style.cashIn}>+{formatCurrency(figures.cash_in_total)}</span>
                {" / "}
                <span className={style.cashOut}>-{formatCurrency(figures.cash_out_total)}</span>
              </div>
            </div>
          </div>

          <div className={style.bottomGrid}>
            <div className={style.section}>
              <h2 className={style.sectionTitle}>Log Cash In / Out</h2>
              <form className={style.movementForm} onSubmit={submitMovement}>
                <div className={style.typeToggle}>
                  <button
                    type="button"
                    className={`${style.typeBtn} ${movementType === "in" ? style.typeBtnActiveIn : ""}`}
                    onClick={() => setMovementType("in")}
                  >
                    Cash In
                  </button>
                  <button
                    type="button"
                    className={`${style.typeBtn} ${movementType === "out" ? style.typeBtnActiveOut : ""}`}
                    onClick={() => setMovementType("out")}
                  >
                    Cash Out
                  </button>
                </div>
                <input
                  className={style.input}
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <input
                  className={style.input}
                  type="text"
                  placeholder="Reason (e.g. Change fund top-up, Paid electricity bill)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <button className={style.submitBtn} type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Log Movement"}
                </button>
              </form>
            </div>

            <div className={style.section}>
              <h2 className={style.sectionTitle}>Today's Movements</h2>
              {movements.length === 0 ? (
                <p className={style.emptyText}>No cash movements logged today.</p>
              ) : (
                <div className={style.movementList}>
                  {movements.map((m) => (
                    <div key={m.cash_movement_id} className={style.movementRow}>
                      {editingId === m.cash_movement_id ? (
                        <>
                          <input
                            className={style.inlineInput}
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                          />
                          <input
                            className={style.inlineInput}
                            type="text"
                            value={editReason}
                            onChange={(e) => setEditReason(e.target.value)}
                          />
                          <button className={style.smallBtn} onClick={() => saveEdit(m.cash_movement_id)}>Save</button>
                          <button className={style.smallBtnGhost} onClick={cancelEdit}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <span className={m.movement_type === "in" ? style.cashIn : style.cashOut}>
                            {m.movement_type === "in" ? "+" : "-"}{formatCurrency(m.amount)}
                          </span>
                          <span className={style.movementReason}>{m.reason}</span>
                          <span className={style.movementTime}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          <button className={style.smallBtnGhost} onClick={() => startEdit(m)}>Edit</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Close Day confirmation */}
      {showCloseDayModal && figures && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Close Today's Books</h2>
            <p className={style.modalText}>
              This locks today's figures permanently — {formatCurrency(figures.balance)} closing balance,
              {" "}{figures.order_count} order{figures.order_count === 1 ? "" : "s"}. This cannot be undone.
            </p>
            <div className={style.modalButtons}>
              <button className={style.smallBtnGhost} onClick={() => setShowCloseDayModal(false)}>Cancel</button>
              <button className={style.closeDayBtn} onClick={closeDay} disabled={isClosingDay}>
                {isClosingDay ? "Closing…" : "Close Day"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History panel */}
      {showHistoryModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Closed Days</h2>
            {isLoadingHistory ? (
              <p className={style.emptyText}>Loading…</p>
            ) : history.length === 0 ? (
              <p className={style.emptyText}>No closed days yet.</p>
            ) : (
              <div className={style.historyList}>
                {history.map((h) => (
                  <div key={h.closure_id} className={style.historyRow}>
                    <span className={style.historyDate}>{h.closure_date}</span>
                    <span className={style.historyBalance}>{formatCurrency(h.closing_balance)}</span>
                    <span className={style.historyMeta}>
                      {h.order_count} order{h.order_count === 1 ? "" : "s"} · {formatCurrency(h.total_sales_amount)} total
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className={style.modalButtons}>
              <button className={style.smallBtnGhost} onClick={() => setShowHistoryModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashDrawerPage;
